/**
 * L1-FE-04 — Duplicate Upload Prompt (Merge / Replace / Cancel).
 *
 * Surfaced when POST /uploads/{type} returns 409 DUPLICATE_UPLOAD_CONFLICT.
 * The 409 envelope carries `existing_upload_id`, `existing_sha256`, and
 * `incoming_sha256` in `details`; the caller stashes the incoming `File`
 * alongside so this component can re-POST it via /uploads/{id}/replace.
 *
 * Three actions:
 *   - **Replace** — POSTs the new file to /uploads/{id}/replace with the
 *     reviewer's reason text. X-Reason is required by the BE because
 *     replacing a prior upload is audit-critical.
 *   - **Cancel** — closes the drawer; no BE call.
 *   - **Merge** — rendered disabled with an explanatory tooltip. Merging
 *     two versions of the same regulatory source file isn't meaningful
 *     in Layer 1 — each (batch, type) has a single source of truth, and
 *     "combine rows from both" requires upload-aware dedupe that lives
 *     in Layer 2. Keeping the button visible (but disabled) sets the
 *     right expectation that the option exists in the design space; the
 *     copy explains why it isn't usable today.
 */

import { isApiError } from "@compliance/domain";
import {
  Alert,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Drawer,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconArrowsExchange,
  IconCheck,
  IconCopyCheck,
  IconShieldCheck,
  IconX,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useReplaceUpload, useUpload } from "../../api/hooks";
import type { UploadRowError, UploadType } from "../../api/types";
import { useBranding, useUploadTypeLabels } from "../../auth/useBranding";
import { formatPacificDisplay } from "../../lib/datetime";
import { invalidateBatchData, refreshInBackground } from "../../lib/invalidate";
import { queryKeys } from "../../lib/queryKeys";
import { ingestFailed } from "../../lib/uploadTypes";
import { UploadRowErrorList } from "./UploadRowErrorList";

/** Conflict context the caller hands to the drawer when the 409 fires. */
export type DuplicateUploadConflict = {
  existingUploadId: string;
  existingSha256: string;
  incomingSha256: string;
  incomingFile: File;
  uploadType: UploadType;
  /** Reporting period (YYYY-MM) — must match the displaced upload on replace. */
  period: string;
};

// Matches the 3-char minimum used by ReasonModal and the other audit-reason
// inputs (the BE only requires a non-empty X-Reason).
const MIN_REASON_CHARS = 3;

export function DuplicateUploadPrompt({
  conflict,
  opened,
  onClose,
}: {
  conflict: DuplicateUploadConflict | null;
  opened: boolean;
  onClose: () => void;
}) {
  const typeLabels = useUploadTypeLabels();
  const branding = useBranding();
  const [reason, setReason] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  // The replacement is itself parsed — if it comes back rejected, surface its
  // row errors here rather than closing the drawer (the GET detail page can't
  // show them; this is the only place the BE returns them for this flow).
  const [replacementErrors, setReplacementErrors] = useState<UploadRowError[] | null>(null);
  const queryClient = useQueryClient();
  const replaceMutation = useReplaceUpload();

  // Pull the existing upload's metadata so the diff has more than just
  // SHAs to compare. The query is skipped when no conflict is open.
  const existingQ = useUpload(conflict?.existingUploadId);

  if (!conflict) return null;

  const reasonValid = reason.trim().length >= MIN_REASON_CHARS;
  const isSubmitting = replaceMutation.isPending;
  const existing = existingQ.data;

  const onReplace = async () => {
    if (!reasonValid) {
      setSubmitError(
        `Reason must be at least ${MIN_REASON_CHARS} characters — it's recorded in the audit log.`,
      );
      return;
    }
    setSubmitError(null);
    setReplacementErrors(null);
    try {
      const replacement = await replaceMutation.mutateAsync({
        existingUploadId: conflict.existingUploadId,
        uploadType: conflict.uploadType,
        period: conflict.period,
        file: conflict.incomingFile,
        reason: reason.trim(),
      });
      // When the replacement didn't parse clean it didn't replace anything —
      // keep the drawer open and show the row errors instead of closing. Only
      // invalidate the uploads list here (the prior upload wasn't superseded).
      if (ingestFailed(replacement)) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.uploads.all });
        setReplacementErrors(replacement.row_errors);
        notifications.show({
          title: "Replacement rejected",
          message: `${conflict.incomingFile.name}: ${replacement.row_errors.length} error${
            replacement.row_errors.length === 1 ? "" : "s"
          } — the prior upload was not replaced.`,
          color: "red",
          icon: <IconX size={18} />,
          autoClose: 5000,
        });
        return;
      }
      refreshInBackground(
        Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.uploads.all }),
          // If the replaced upload already fed a batch, the replace can move that
          // batch's data version / calc results / issues / reports / audit — so
          // refresh the whole batch surface (which also drops the cache-forever
          // data series). When it wasn't batch-linked yet, just drop the series.
          existing?.batch_id
            ? invalidateBatchData(queryClient, existing.batch_id)
            : queryClient.invalidateQueries({ queryKey: queryKeys.data.seriesAll() }),
        ]),
      );
      // A clean replacement is "parsed" until a reviewer accepts it — don't
      // imply it's already in the dataset.
      const accepted = replacement.status === "accepted";
      notifications.show({
        title: "Upload replaced",
        message: accepted
          ? `${conflict.incomingFile.name} now supersedes ${conflict.existingUploadId}.`
          : `${conflict.incomingFile.name} supersedes ${conflict.existingUploadId} — parsed cleanly, awaiting reviewer acceptance.`,
        color: "teal",
        icon: <IconCheck size={18} />,
        autoClose: 3500,
      });
      onClose();
    } catch (err) {
      if (isApiError(err)) {
        setSubmitError(`${err.code}: ${err.message}`);
      } else {
        setSubmitError((err as Error).message);
      }
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={520}
      withCloseButton
      closeOnEscape={!isSubmitting}
      closeOnClickOutside={!isSubmitting}
      title={
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon variant="light" color="yellow" radius="md" size={32}>
            <IconCopyCheck size={18} />
          </ThemeIcon>
          <Stack gap={0}>
            <Title order={4}>An upload already exists for this period</Title>
            <Text size="xs" c="dimmed">
              {typeLabels[conflict.uploadType]}
            </Text>
          </Stack>
        </Group>
      }
    >
      <Stack gap="md">
        <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">
            The system already holds an active upload of this type for this batch. Choose how to
            resolve the conflict — every option is recorded in the audit log.
          </Text>
        </Alert>

        <Stack gap="xs">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.06em">
            Existing upload
          </Text>
          <Paper p="sm" radius="sm" withBorder>
            <Stack gap={4}>
              <Group justify="space-between" wrap="nowrap">
                <Text size="sm" fw={500} ff="monospace" truncate>
                  {existing?.original_filename ?? conflict.existingUploadId}
                </Text>
                {existing ? (
                  <Badge size="xs" variant="light" color="gray">
                    {existing.status}
                  </Badge>
                ) : null}
              </Group>
              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  sha
                </Text>
                <Code fz="xs">{conflict.existingSha256.slice(0, 16)}…</Code>
              </Group>
              {existing ? (
                <Text size="xs" c="dimmed">
                  {existing.row_count != null ? `${existing.row_count} rows · ` : ""}
                  {(existing.content_length / 1024).toFixed(1)} KB · uploaded{" "}
                  {formatPacificDisplay(existing.uploaded_at)} by {existing.uploaded_by}
                </Text>
              ) : (
                <Text size="xs" c="dimmed">
                  {existingQ.isPending ? "Loading existing record…" : "Metadata unavailable"}
                </Text>
              )}
            </Stack>
          </Paper>
        </Stack>

        <Stack gap="xs">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.06em">
            Incoming upload
          </Text>
          <Paper p="sm" radius="sm" withBorder>
            <Stack gap={4}>
              <Text size="sm" fw={500} ff="monospace" truncate>
                {conflict.incomingFile.name}
              </Text>
              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  sha
                </Text>
                <Code fz="xs">{conflict.incomingSha256.slice(0, 16)}…</Code>
              </Group>
              <Text size="xs" c="dimmed">
                {(conflict.incomingFile.size / 1024).toFixed(1)} KB ·{" "}
                {conflict.incomingFile.type || "text/csv"}
              </Text>
            </Stack>
          </Paper>
        </Stack>

        <Divider />

        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Reason for replacing
          </Text>
          <Text size="xs" c="dimmed">
            Replacing the prior upload is recorded with your stated reason for audit. Future
            reviewers (and regulators on inspection) will see exactly why the original record was
            superseded.
          </Text>
          <Textarea
            placeholder={`e.g. ${branding.custody_party_label} re-issued the March statement after a billing correction.`}
            minRows={3}
            autosize
            maxRows={6}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            disabled={isSubmitting}
            error={
              reason.length > 0 && !reasonValid
                ? `At least ${MIN_REASON_CHARS} characters required`
                : undefined
            }
          />
        </Stack>

        {submitError ? (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">{submitError}</Text>
          </Alert>
        ) : null}

        {replacementErrors && replacementErrors.length > 0 ? (
          <Alert
            color="red"
            variant="light"
            icon={<IconX size={16} />}
            title={`Replacement rejected — ${replacementErrors.length} error${
              replacementErrors.length === 1 ? "" : "s"
            }`}
          >
            <Stack gap={4}>
              <Text size="xs">
                The prior upload was not replaced. Fix the source file and try again.
              </Text>
              <UploadRowErrorList errors={replacementErrors} max={20} />
            </Stack>
          </Alert>
        ) : null}

        <Divider />

        <Stack gap="xs">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.06em">
            Resolution
          </Text>
          <Group gap="sm" wrap="wrap">
            <Tooltip
              label={
                reasonValid
                  ? "Replace the prior upload with this file"
                  : `Add a reason (${MIN_REASON_CHARS}+ chars) to enable replace`
              }
              position="top"
            >
              <Button
                color="indigo"
                leftSection={<IconArrowsExchange size={16} />}
                onClick={onReplace}
                loading={isSubmitting}
                disabled={!reasonValid || isSubmitting}
              >
                Replace
              </Button>
            </Tooltip>

            <Tooltip
              label="Merging requires upload-aware row dedupe — available in Layer 2. Each (batch, type) currently expects one source-of-truth file; use Replace to substitute a corrected version."
              multiline
              w={320}
              position="top"
            >
              <Box>
                <Button variant="default" disabled leftSection={<IconShieldCheck size={16} />}>
                  Merge
                </Button>
              </Box>
            </Tooltip>

            <Button
              variant="subtle"
              color="gray"
              onClick={onClose}
              disabled={isSubmitting}
              leftSection={<IconX size={16} />}
            >
              Cancel
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            Cancel closes this prompt without uploading. Nothing is written to the audit log on
            cancel.
          </Text>
        </Stack>
      </Stack>
    </Drawer>
  );
}
